/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { PageTitle } from './PageTitle';
import {
  useAreaIntroToggle,
  type AreaIntroConfig,
} from './useAreaIntroToggle';

export interface PageTitleWithAreaIntroProps {
  children: ReactNode;
  icon?: ReactNode;
  intro?: AreaIntroConfig | null;
  className?: string;
  'data-testid'?: string;
}

/**
 * PageTitle plus optional info (i) toggle that reveals area help copy.
 *
 * @param props - Title content and optional intro config.
 * @returns Title row and expandable intro panel.
 */
export function PageTitleWithAreaIntro({
  children,
  icon,
  intro,
  className,
  'data-testid': testId,
}: PageTitleWithAreaIntroProps) {
  const { accessory, panel } = useAreaIntroToggle(intro);

  return (
    <div className="flex flex-col gap-(--space-3)">
      <div className="flex items-center gap-(--space-2)">
        <PageTitle icon={icon} className={className} data-testid={testId}>
          {children}
        </PageTitle>
        {accessory}
      </div>
      {panel}
    </div>
  );
}
